import type { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import type { Workspace } from '@blocksuite/affine/store';
import { ArrowRightBigIcon } from '@blocksuite/icons/rc';

import type { useNavigateHelper } from '../components/hooks/use-navigate-helper';
import type { WorkspaceDialogService } from '../modules/dialogs';
import { navigationActionId } from '../modules/streamlain/copilot/workspace-navigation-catalog';
import type { WorkbenchService } from '../modules/workbench';
import {
  AffineCommandRegistry,
  PreconditionStrategy,
  registerAffineCommand,
} from './registry';

export function registerAffineNavigationCommands({
  t,
  docCollection,
  navigationHelper,
  workspaceDialogService,
  workbenchService,
  collectionsEnabled,
  tagsEnabled,
}: {
  t: ReturnType<typeof useI18n>;
  navigationHelper: ReturnType<typeof useNavigateHelper>;
  docCollection: Workspace;
  workspaceDialogService: WorkspaceDialogService;
  workbenchService?: WorkbenchService;
  collectionsEnabled: boolean;
  tagsEnabled: boolean;
}) {
  const unsubs: Array<() => void> = [];

  if (collectionsEnabled) {
    unsubs.push(
      registerAffineCommand({
        id: 'affine:goto-collection-list',
        category: 'affine:navigation',
        icon: <ArrowRightBigIcon />,
        label: 'Go to Collection List',
        run() {
          track.$.cmdk.navigation.navigate({
            to: 'collectionList',
          });

          navigationHelper.jumpToCollections(docCollection.id);
        },
      })
    );
  }

  if (tagsEnabled) {
    unsubs.push(
      registerAffineCommand({
        id: 'affine:goto-tag-list',
        category: 'affine:navigation',
        icon: <ArrowRightBigIcon />,
        label: 'Go to Tag List',
        run() {
          track.$.cmdk.navigation.navigate({
            to: 'tagList',
          });

          navigationHelper.jumpToTags(docCollection.id);
        },
      })
    );
  }

  unsubs.push(
    registerAffineCommand({
      id: 'affine:goto-workspace',
      category: 'affine:navigation',
      icon: <ArrowRightBigIcon />,
      label: t['com.streamlain.cmdk.affine.navigation.goto-workspace'](),
      run() {
        track.$.cmdk.navigation.navigate({
          to: 'workspace',
        });

        workbenchService?.workbench.openWorkspaceSelector();
      },
    })
  );

  // The shortcut invokes the same current UI-list definition as search and clicks.
  unsubs.push(
    registerAffineCommand({
      id: 'affine:open-settings',
      category: 'affine:navigation',
      preconditionStrategy: PreconditionStrategy.Never,
      icon: <ArrowRightBigIcon />,
      label: '',
      keyBinding: '$mod+,',
      run() {
        const view = workbenchService?.workbench.activeView$.value;
        if (!view) return;
        const definition = AffineCommandRegistry.get(
          navigationActionId(
            { workspaceId: docCollection.id, viewId: view.id },
            'workspace-navigation',
            'settings'
          )
        )?.navigation?.definition;
        if (definition?.available()) return definition.activate({});
      },
    })
  );

  unsubs.push(
    registerAffineCommand({
      id: 'affine:open-account',
      category: 'affine:navigation',
      icon: <ArrowRightBigIcon />,
      label: t['com.streamlain.cmdk.affine.navigation.open-account-settings'](),
      run() {
        track.$.cmdk.settings.openSettings({ to: 'account' });
        workspaceDialogService.open('setting', {
          activeTab: 'account',
        });
      },
    })
  );

  return () => {
    unsubs.forEach(unsub => unsub());
  };
}
